import { Style, logNumber, registerObjectClassFormatter } from '../core/logging.ts';
import { identity } from '../identity.ts';
import { isNumber } from '../identity/type_guards.ts';
import type { Dot } from './measure.ts';
import { Measure } from './measure.ts';
import type { IMeta } from './meta.ts';
import type { Proportion, Quantity, RatioOf } from './quantity.ts';
import type { Unit } from './unit.ts';

// DNS!!! Make Measure's const objects or maybe private members to protect them from overwriting.

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class Scalar<Q extends Quantity> extends Measure<Q, number> {
  static of<Q extends Quantity>(units: Unit<Q>): IMeta<Scalar<Q>, Q, number, number> {
    return {
      units,
      build: (r) => new Scalar(units, r),
      guard: isNumber,
      serialize: identity,
      deserialize: identity,
    } as const;
  }

  static from<Q extends Quantity>(units: Unit<Q>, value: number | Scalar<Q>) {
    return value instanceof Scalar
      ? value.inUnits(units)
      : new Scalar(units, value / units.rawToUnits);
  }

  override compare(other: this, fn?: (t: number, o: number) => number): number {
    fn ??= (a, b) => a - b; // numericCompare<number>;
    return fn(this.raw, other.raw);
  }

  override toString(options?: { units?: Unit<Q>; decimals?: number; informal?: boolean }) {
    const units = options?.units ?? this.units;
    const decimals = options?.decimals ?? 2;
    // TODO: Associate Intl.NumberFormat options with units.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#options
    // TODO: Generalize left-side unit labels?
    if (units.symbol === '$') {
      const result = this.value(units).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      const informal = options?.informal ?? false;
      return (
        (informal ? (result.endsWith('.00') ? result.slice(0, -3) : result) : undefined) ?? result
      );
    } else {
      return `${this.value(units).toLocaleString(undefined, {
        minimumFractionDigits: options?.decimals,
        maximumFractionDigits: decimals,
      })}${units.symbol}`;
    }
  }

  static buildFromRaw<Q extends Quantity>(units: Unit<Q>, raw: number) {
    return new Scalar(units, raw);
  }

  protected build(units: Unit<Q>, raw: number): this;
  protected build<Q2 extends Quantity>(units: Unit<Q2>, raw: number): Scalar<Q2>;
  protected build<Q2 extends Quantity>(units: Unit<Q2>, raw: number): this | Scalar<Q2> {
    return new Scalar(units, raw);
  }

  protected transform(
    fn: (q: number, a: number, b: number) => number,
    argA?: number,
    argB?: number,
  ) {
    return fn(this.raw, argA ?? NaN, argB ?? NaN);
  }

  protected reduce(fn: (accumulator: number, q: number) => number, initial: number) {
    return fn(initial, this.raw);
  }
}

export interface Scalar<Q extends Quantity> {
  dot<Q2 extends Quantity>(quantity: Measure<Q2, number>): Scalar<Dot<Q, Q2>>;
  per(quantity: Scalar<Q> | this, units?: Unit<Proportion>): Scalar<Proportion>;
  per<Q2 extends Quantity>(quantity: Measure<Q2, number>): Scalar<RatioOf<Q, Q2>>;
}

registerObjectClassFormatter(Scalar, (v: Scalar<Quantity>, b) => {
  if (v.units.symbol !== '$') {
    b.override(Style.number, logNumber(v.value(), 2)).override(Style.subtle, v.units.symbol);
  } else if (v.value() < 0) {
    b.override(Style.number, '-')
      .override(Style.subtle, v.units.symbol)
      .override(Style.number, logNumber(-v.value(), 2, true));
  } else {
    b.override(Style.subtle, v.units.symbol).override(Style.number, logNumber(v.value(), 2, true));
  }
  return { result: 'builder' } as const;
});
